import React, { type FC } from "react";
import { useCustomTranslation } from "clientState/AuthFlow/hooks";
import { useModalState } from "clientState/AuthFlow/providers/ModalScreenProvider";
import {
  screenNames,
  useScreenActions,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { Box, SystemModalContent } from "@edreamsodigeo/prisma-design-system";

import Footer from "../Footer";
import { MODAL_STYLES } from "../Helpers/ModalStyles";
import ScreenHeader, { type ScreenHeaderProps } from "../ScreenHeader";
import { animatedContentStyles } from "../styles";
import {
  type PasswordlessBodyProps,
  type PasswordlessContentProps,
} from "./contract";
import PasswordlessBody from "./PasswordlessBody";

const { PASSWORDLESS } = screenNames;
export const PASSWORDLESS_WITH_PASSWORD_CLICK_ID = `${PASSWORDLESS}_click:with-password`;

const PasswordlessContent: FC<PasswordlessContentProps> = ({
  onSuccess,
  isInitialScreen,
  animationIsActive,
}) => {
  const { customTranslation } = useCustomTranslation();
  const { titleColor } = useModalState();
  const screenActions = useScreenActions();
  const { content: button } = customTranslation({
    customKey: "authentication.passwordless.submit",
    defaultKey: "authentication.passwordless.submit",
  });
  const { content: title, showComponent: showTitle } = customTranslation({
    customKey: "authentication.passwordless.title",
    defaultKey: "authentication.passwordless.title",
  });
  const { content: subtitle, showComponent: showSubtitle } = customTranslation({
    customKey: "authentication.passwordless.subtitle",
    defaultKey: "authentication.passwordless.subtitle",
  });
  const { content: emailLabelForm } = customTranslation({
    customKey: "authentication.passwordless.emailLabelForm",
    defaultKey: "auth.form.email.label",
  });
  const { content: signinLinkContent, showComponent: showSigninLink } =
    customTranslation({
      customKey: "authentication.passwordless.withPasswordLabel",
      defaultKey: "authentication.passwordless.withPassword",
    });
  const { content: signupLinkTitle, showComponent: showSignupLinkTitle } =
    customTranslation({
      customKey: "authentication.passwordless.signup.title",
      defaultKey: "authentication.signin.signup.title",
    });
  const { content: signupLinkLabel, showComponent: showSignupLinkLabel } =
    customTranslation({
      customKey: "authentication.passwordless.signup.linkLabel",
      defaultKey: "authentication.signin.signup.link",
    });

  const passwordlessBodyProps: PasswordlessBodyProps = {
    signinLink: {
      trackingId: PASSWORDLESS_WITH_PASSWORD_CLICK_ID,
      content: signinLinkContent,
    },
    showSigninLink,
    signupLink: {
      title: signupLinkTitle,
      linkLabel: signupLinkLabel,
    },
    showSignupLink: showSignupLinkTitle && showSignupLinkLabel,
    submitButtonLabel: button,
    onSuccess,
    emailLabelForm,
    isInitialScreen,
    isLoginSyncEnabled: true,
    ...screenActions,
  };
  const passwordlessHeaderProps: ScreenHeaderProps = {
    title: {
      content: title,
      color: titleColor,
    },
    subtitle,
    showTitle,
    showSubtitle,
  };

  return (
    <SystemModalContent>
      <Box
        sx={{
          ...MODAL_STYLES,
          ...(animationIsActive ? animatedContentStyles : {}),
        }}
      >
        <ScreenHeader {...passwordlessHeaderProps} />
        <PasswordlessBody {...passwordlessBodyProps} />
        <Footer
          withGeneralTC
          withRecaptchaPolicy={showSigninLink}
          screen={PASSWORDLESS}
        />
      </Box>
    </SystemModalContent>
  );
};

export default PasswordlessContent;
